import { useMemo, useState } from "react";

import {
    DateTimePicker,
    DialogWrapper,
    LabelWrapper,
    Row,
    StatColumn,
    StatProps,
    StatRawValue,
    TagTextAlert,
    Text
} from "@bridgesplit/ui";
import { AppDialog, useAppDialog } from "app/utils";
import { BalanceDisplay, DialogHeader, TokenInputWithPrice, useBalanceChecker } from "app/components/common";
import {
    convertDateJsToUnix,
    convertUnixToDateJs,
    formatDate,
    formatSeconds,
    formatTokenAmount,
    formatUsd,
    nearestHour,
    TIME
} from "@bridgesplit/utils";
import {
    getRewardsScheduleStats,
    ParsedVaultRewardsScheduleExpanded,
    useOraclePrices,
    useStrategyDurations
} from "@bridgesplit/abf-react";

import { EditRewardsForm } from "../common/types";
import { ManagerEditRewardsCta } from "./ManagerEditRewardsCta";
import { ManagerViewProvider, useManagerViewContext } from "../../ManagerViewContext";
import { getManagerEditRewardFormValidation } from "./common";
export interface ManagerEditRewardsProps {
    form: EditRewardsForm;
    setForm: (form: EditRewardsForm) => void;
    schedule: ParsedVaultRewardsScheduleExpanded | undefined;
    setStep: (step: RewardStep) => void;
    now: number;
}

enum RewardStep {
    EditRewardsConfig = "EditRewardsConfig"
}

/** Threshold in days after which a schedule is considered significantly past its end time */
const SIGNIFICANTLY_PAST_END_TIME_DAYS = 14;

const initialEditForm: EditRewardsForm = {
    rewardToken: undefined,
    rewardAmount: undefined,
    rewardEndTime: null
};

const REWARD_STEPS = [RewardStep.EditRewardsConfig];

export function ManagerEditRewardsDialog() {
    const { getData } = useAppDialog();
    const data = getData(AppDialog.ManagerEditRewards);
    const { strategyDurations } = useStrategyDurations();

    const [form, setForm] = useState<EditRewardsForm>({
        ...initialEditForm,
        rewardEndTime: convertUnixToDateJs(data?.schedule?.rewardEndTime),
        rewardToken: data?.schedule?.rewardMetadata
    });
    const [step, setStep] = useState<RewardStep>(RewardStep.EditRewardsConfig);

    const now = useMemo(() => nearestHour(), []);

    const stepIndex = REWARD_STEPS.indexOf(step);
    const content = useMemo(() => {
        const formProps: ManagerEditRewardsProps = {
            form,
            setForm,
            schedule: data?.schedule,
            setStep,
            now
        };

        switch (step) {
            case RewardStep.EditRewardsConfig:
                return <EditRewardsConfig {...formProps} />;
            default:
                return null;
        }
    }, [form, data?.schedule, step, now]);

    return (
        <ManagerViewProvider vaultExpanded={data?.vaultExpanded} strategyDurations={strategyDurations}>
            <DialogWrapper maxWidth={500}>
                <DialogHeader header="Edit Rewards" description="Edit the schedule for the rewards" />
                {content}
                {stepIndex === REWARD_STEPS.length - 1 ? null : null}
            </DialogWrapper>
        </ManagerViewProvider>
    );
}

function EditRewardsConfig(props: ManagerEditRewardsProps) {
    const { form, setForm, setStep, schedule, now } = props;

    const { balance, insufficientBalance, isLoading } = useBalanceChecker({
        mint: form.rewardToken?.mint,
        amount: form.rewardAmount
    });

    return (
        <>
            <LabelWrapper label="Reward Token">
                <TokenInputWithPrice
                    selectToken={null}
                    metadata={form?.rewardToken}
                    value={form?.rewardAmount}
                    setValue={(val: number | undefined) => {
                        setForm({
                            ...form,
                            rewardAmount: val
                        });
                    }}
                    belowInput={
                        <BalanceDisplay
                            balance={balance}
                            insufficientBalance={insufficientBalance}
                            isLoading={isLoading}
                            setMax={(amount) => setForm({ ...form, rewardAmount: amount })}
                            metadata={form?.rewardToken}
                        />
                    }
                />
            </LabelWrapper>
            <LabelWrapper label="End Time">
                <DateTimePicker
                    value={form.rewardEndTime}
                    onChange={(val) => setForm({ ...form, rewardEndTime: val })}
                    sx={{
                        width: "100%"
                    }}
                />
            </LabelWrapper>
            <EditStats form={form} setForm={setForm} setStep={setStep} schedule={schedule} now={now} />
            <EditWarnings {...props} />
            <ManagerEditRewardsCta {...props} />
        </>
    );
}

function TokenAmountWithUsd({
    amount,
    symbol,
    usdPrice
}: {
    amount: number;
    symbol: string;
    usdPrice: number | undefined;
}) {
    return (
        <Row spacing={0.5} sx={{ display: "inline-flex" }}>
            <Text variant="body1">{formatTokenAmount(amount, { symbol })}</Text>
            {usdPrice !== undefined && (
                <Text variant="body1" color="caption">
                    ({formatUsd(amount * usdPrice)})
                </Text>
            )}
        </Row>
    );
}

function EditStats({ form, schedule, now }: ManagerEditRewardsProps) {
    const { vaultExpanded } = useManagerViewContext();
    const { getOracle } = useOraclePrices(schedule ? [schedule.rewardMetadata.mint] : undefined);

    if (!schedule || !form) {
        return null;
    }

    const formError = getManagerEditRewardFormValidation(form, schedule);
    if (formError) {
        return null;
    }

    const price = getOracle(schedule.rewardMetadata.mint);
    const symbol = schedule.rewardMetadata.symbol;
    const usdPrice = price?.usdPrice;

    const statsData = getEditRewardStats({
        form,
        schedule,
        now,
        vaultRewardsEmittedAmounts: vaultExpanded?.vaultRewardsEmittedAmounts
    });

    const emissionsPerWeekValue: [StatRawValue, StatRawValue] = [
        <TokenAmountWithUsd amount={statsData.oldEmissionsPerWeek} symbol={symbol} usdPrice={usdPrice} />,
        <TokenAmountWithUsd amount={statsData.newEmissionsPerWeek} symbol={symbol} usdPrice={usdPrice} />
    ];

    const stats: StatProps[] = [
        {
            caption: "Undistributed rewards",
            value: [
                formatTokenAmount(statsData.oldUnemittedRewards, { symbol }),
                formatTokenAmount(statsData.newUnemittedRewards, { symbol })
            ],
            hide: !form.rewardAmount
        },
        {
            caption: "End time",
            value: [formatDate(statsData.oldEndTime), formatDate(statsData.newEndTime ?? statsData.oldEndTime)],
            hide: !form.rewardEndTime
        },
        {
            caption: "Emissions / week",
            value: emissionsPerWeekValue,
            hide: !statsData.hasRateChange
        }
    ];

    return <StatColumn stats={stats} loading={!schedule || !form} />;
}

function EditWarnings(props: ManagerEditRewardsProps) {
    const { form, schedule, now } = props;

    if (!schedule || !form || getManagerEditRewardFormValidation(form, schedule)) {
        return null;
    }

    const { contextAlert, changeAlert } = getEditWarningAlerts(form, schedule, now);

    if (!contextAlert && !changeAlert) {
        return null;
    }

    return (
        <>
            {contextAlert && (
                <TagTextAlert icon={contextAlert.icon} color={contextAlert.color}>
                    {contextAlert.text}
                </TagTextAlert>
            )}
            {changeAlert && (
                <TagTextAlert icon={changeAlert.icon} color={changeAlert.color}>
                    {changeAlert.text}
                </TagTextAlert>
            )}
        </>
    );
}

function getEditRewardStats({
    form,
    schedule,
    now,
    vaultRewardsEmittedAmounts
}: {
    form: EditRewardsForm;
    schedule: ParsedVaultRewardsScheduleExpanded;
    now: number;
    vaultRewardsEmittedAmounts?: Record<number, number>;
}) {
    const { unemittedRewards: oldUnemittedRewards, emissionsPerWeek: oldEmissionsPerWeek } = getRewardsScheduleStats(
        schedule,
        vaultRewardsEmittedAmounts
    );

    const oldEndTime = schedule.rewardEndTime;
    const newEndTime = convertDateJsToUnix(form.rewardEndTime) ?? oldEndTime;

    const newRemainingTime = Math.max(newEndTime - now, 0);
    const newRewardAmount = form.rewardAmount || 0;
    const newUnemittedRewards = oldUnemittedRewards + newRewardAmount;
    const newEmissionsPerWeek = newRemainingTime > 0 ? (newUnemittedRewards / newRemainingTime) * TIME.WEEK : 0;

    const hasRateChange = !!form.rewardAmount || (!!form.rewardEndTime && newEndTime !== oldEndTime);

    return {
        oldUnemittedRewards,
        newUnemittedRewards,
        oldEmissionsPerWeek,
        newEmissionsPerWeek,
        hasRateChange,
        oldEndTime,
        newEndTime
    };
}

type AlertInfo = { icon: "warning" | "tooltip"; color: "warning" | "info"; text: string };

function getEditWarningAlerts(
    form: EditRewardsForm,
    schedule: ParsedVaultRewardsScheduleExpanded,
    now: number
): { contextAlert: AlertInfo | null; changeAlert: AlertInfo | null } {
    const secondsPastEndTime = now - schedule.rewardEndTime;
    const significantlyPastEndTime = secondsPastEndTime > SIGNIFICANTLY_PAST_END_TIME_DAYS * TIME.DAY;

    const oldEndTime = schedule.rewardEndTime;
    const newEndTime = convertDateJsToUnix(form.rewardEndTime) ?? oldEndTime;

    const endTimeChanged = !!newEndTime && newEndTime !== oldEndTime;
    const diffSeconds = endTimeChanged ? newEndTime - oldEndTime : 0;
    const secondsFromToday = Math.max(newEndTime - now, 0);

    const isEditingTimeOnly = form.rewardAmount === 0 || form.rewardAmount === undefined;

    const contextAlert: AlertInfo | null = significantlyPastEndTime
        ? {
              icon: "warning",
              color: "warning",
              text: `This reward schedule ended ${formatSeconds(secondsPastEndTime, {
                  maxUnit: "day"
              })} ago. You are editing historical values.`
          }
        : null;

    let changeAlert: AlertInfo | null = null;
    if (endTimeChanged && diffSeconds < 0) {
        changeAlert = {
            icon: "warning",
            color: "warning",
            text: isEditingTimeOnly
                ? `The schedule will end ${formatSeconds(Math.abs(diffSeconds), {
                      maxUnit: "day"
                  })} sooner. No new rewards are being added, only the end time is changed.`
                : `The schedule will end ${formatSeconds(Math.abs(diffSeconds), {
                      maxUnit: "day"
                  })} sooner. Rewards will be distributed faster.`
        };
    } else if (endTimeChanged && diffSeconds > 0 && significantlyPastEndTime) {
        changeAlert = {
            icon: "tooltip",
            color: "info",
            text: isEditingTimeOnly
                ? `The schedule will resume and run for ${formatSeconds(secondsFromToday, {
                      maxUnit: "day"
                  })} from now. No new rewards are being added, only the end time is changed.`
                : `The schedule will resume and run for ${formatSeconds(secondsFromToday, {
                      maxUnit: "day"
                  })} from now.`
        };
    } else if (endTimeChanged && diffSeconds > 0) {
        changeAlert = {
            icon: "tooltip",
            color: "info",
            text: isEditingTimeOnly
                ? `The schedule will run ${formatSeconds(Math.abs(diffSeconds), {
                      maxUnit: "day"
                  })} longer. No new rewards are being added, only the end time is changed.`
                : `The schedule will run ${formatSeconds(Math.abs(diffSeconds), {
                      maxUnit: "day"
                  })} longer.`
        };
    }

    return { contextAlert, changeAlert };
}
